import { ExternalLink } from '@/components/common/external-link';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { Popover, PopoverAnchor, PopoverContent } from '@heidi/design-system/popover';
import { typographyVariant } from '@heidi/design-system/typography';
import { type Editor } from '@tiptap/react';
import { ExternalLink as OpenIcon, Pencil, Unlink } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { normalizeLinkHref } from '../normalize-link-href';
import { LinkEditorPopover } from '../text-menu/link-editor-popover';
import { getCaretLinkAnchor } from './link-anchor';
import { useLinkMenu } from './use-link-menu';

/**
 * Hover / caret link menu for the markdown-native editor. A link inside the
 * editable surface can't be opened by clicking (the browser suppresses anchor
 * navigation in `contenteditable`, and the mark sets `openOnClick: false`), so
 * this floating card is the surface for Open / Edit / Remove. Mounted as a
 * sibling of the text menu; see `useLinkMenu` for the detection wiring.
 */
export const MarkdownNativeLinkMenu = ({ editor }: { editor: Editor }) => {
  const intl = useIntl();
  const contentRef = useRef<HTMLDivElement>(null);
  const restoreEditorFocusRef = useRef(false);
  const {
    anchor,
    anchorRef,
    isEditing,
    setOpen,
    startEditing,
    removeLink,
    onCardMouseEnter,
    onCardMouseLeave,
  } = useLinkMenu(editor);

  const href = anchor?.getAttribute('href') ?? '';
  const normalizedHref = normalizeLinkHref(href);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // `defaultPrevented`: an editor keybinding that consumed the press
      // (e.g. Tab indenting a list item) keeps it; the card must not also
      // capture focus on the same keystroke.
      if (
        event.defaultPrevented ||
        event.key !== 'Tab' ||
        event.shiftKey ||
        !anchor ||
        getCaretLinkAnchor(editor) !== anchor
      ) {
        return;
      }

      const firstAction = contentRef.current?.querySelector<HTMLElement>(
        'a[href], button:not(:disabled)'
      );
      if (firstAction) {
        event.preventDefault();
        restoreEditorFocusRef.current = true;
        firstAction.focus();
      }
    };

    const dom = editor.view.dom;
    dom.addEventListener('keydown', onKeyDown);
    return () => dom.removeEventListener('keydown', onKeyDown);
  }, [editor, anchor]);

  return (
    <Popover
      open={anchor !== null}
      onOpenChange={(open) => {
        if (!open) {
          setOpen(false);
        }
      }}
    >
      <PopoverAnchor virtualRef={anchorRef} />
      <PopoverContent
        ref={contentRef}
        align="start"
        className="w-80 p-2"
        aria-label={intl.formatMessage({
          defaultMessage: 'Link options',
          id: 'PozzAdUeN9',
        })}
        onOpenAutoFocus={(event) => event.preventDefault()}
        // A pointer close hands focus to whatever was clicked; only a
        // keyboard close (e.g. Escape) may restore the editor below.
        onInteractOutside={() => {
          restoreEditorFocusRef.current = false;
        }}
        onCloseAutoFocus={(event) => {
          if (restoreEditorFocusRef.current) {
            event.preventDefault();
            restoreEditorFocusRef.current = false;
            editor.view.focus();
          }
        }}
        onMouseEnter={onCardMouseEnter}
        onMouseLeave={onCardMouseLeave}
      >
        {isEditing ? (
          <LinkEditorPopover
            editor={editor}
            initialHref={href}
            hasLink
            onClose={() => setOpen(false)}
          />
        ) : (
          <div className="flex items-center gap-1">
            <span
              className={cn(
                typographyVariant({ variant: 'p2' }),
                'flex-1 truncate text-text-link'
              )}
              title={href}
            >
              {href}
            </span>
            {normalizedHref && (
              <Button asChild variant="ghost" size="sm" data-testid="link-menu-open">
                <ExternalLink href={normalizedHref} onClick={() => setOpen(false)}>
                  <OpenIcon />
                  {intl.formatMessage({ defaultMessage: 'Open', id: 'JfG49wNHKP' })}
                </ExternalLink>
              </Button>
            )}
            {/* Edit / Remove mutate the note, so they stay off read-only
                surfaces (e.g. the version-diff viewer); Open is safe there. */}
            {editor.isEditable && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  onMouseDown={(event) => {
                    event.preventDefault();
                    startEditing();
                  }}
                  onClick={(event) => {
                    // Keyboard activation has no preceding mousedown. Mouse
                    // activation starts above, before editor focus/selection
                    // can change and dismiss the card.
                    if (event.detail === 0) {
                      startEditing();
                    }
                  }}
                  aria-label={intl.formatMessage({
                    defaultMessage: 'Edit link',
                    id: 'sq+2TtFwI7',
                  })}
                  data-testid="link-menu-edit"
                >
                  <Pencil />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={removeLink}
                  aria-label={intl.formatMessage({
                    defaultMessage: 'Remove link',
                    id: 'cr1jN833YB',
                  })}
                  data-testid="link-menu-remove"
                >
                  <Unlink />
                </Button>
              </>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
